/** Tips & tricks: a short guide to what Nexus can do. */
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { ModuleIcon } from '../components/ModuleIcon';
import { tipsFor } from './content';

/** `text with code` → text with <code>. */
function Rich({ text }: { text: string }) {
  return (
    <>
      {text.split(/(`[^`]+`)/g).map((part, i) =>
        part.startsWith('`') && part.endsWith('`') && part.length > 1 ? (
          <code key={i} className="rounded-md bg-bg-subtle px-1.5 py-0.5 text-[0.85em]">
            {part.slice(1, -1)}
          </code>
        ) : (
          <Fragment key={i}>{part}</Fragment>
        ),
      )}
    </>
  );
}

export function TipsView() {
  const { t, i18n } = useTranslation();
  const sections = tipsFor(i18n.language);
  return (
    <div className="mx-auto w-full max-w-6xl px-6 py-6 sm:px-10" data-testid="tips">
      <header className="mb-6">
        <h1 className="flex items-center gap-3 text-3xl font-bold tracking-tight">
          <ModuleIcon kind="tips" size="lg" />
          {t('tips.title')}
        </h1>
        <p className="mt-1 text-sm text-fg-muted">{t('tips.intro')}</p>
      </header>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {sections.map((s) => (
          <section
            key={s.id}
            aria-label={s.title}
            className="rounded-2xl border border-border bg-bg p-4"
          >
            <h2 className="mb-2 font-semibold tracking-tight">{s.title}</h2>
            <ul className="flex flex-col gap-2">
              {s.tips.map((tip, i) => (
                <li key={i} className="flex gap-2 text-sm leading-relaxed">
                  <span
                    aria-hidden
                    className="mt-2 size-1.5 shrink-0 rounded-full bg-accent-bright"
                  />
                  <span className="min-w-0 flex-1">
                    <Rich text={tip.text} />
                    {tip.keys && (
                      <kbd className="ml-2 rounded-md border border-border bg-bg-subtle px-1.5 py-0.5 font-sans text-xs whitespace-nowrap">
                        {tip.keys}
                      </kbd>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </div>
  );
}
